import bugsnag from "@bugsnag/js";
import compact from "lodash/compact";
import differenceWith from "lodash/differenceWith";
import filter from "lodash/filter";
import find from "lodash/find";
import includes from "lodash/includes";
import intersection from "lodash/intersection";
import map from "lodash/map";
import pull from "lodash/pull";
import pullAllBy from "lodash/pullAllBy";
import some from "lodash/some";
import sortBy from "lodash/sortBy";
import { MessageDescriptor } from "react-intl";
import { v1 as uuid } from "uuid";
import { PageConfigMapping } from "../../states/customization";
import { TemplateLocale } from "../../types/customization/common";
import { Configuration } from "../../types/customization/configuration";
import {
  Content,
  ContentEditablePicture,
  ContentEditableText,
  ContentPage,
} from "../../types/customization/content";
import { TemplateColorDefinition } from "../../types/customization/template";
import {
  Template,
  TemplateEditablePictureDefinition,
  TemplateElementDefinition,
  TemplatePageDefinition,
} from "../../types/customization/template";
import { LocalizedContent } from "../../types/customization/text";
import { SelectionPhoto } from "../../types/selection/selection";
import { findColorDefinition, findPageDefinition } from "../../utils/customization/finders";
import { findLocalizedContent } from "./localized";
import { DEFAULT_FONT_COLOR } from "./text";

function getZIndex(element: TemplateElementDefinition): number {
  return element.zIndex;
}

function getTypeOrder(element: TemplateElementDefinition): number {
  switch (element.type) {
    case "editable-picture":
      return 10;
    case "editable-text":
      return 0;
    case "forced-image":
      return 11;
    case "forced-text":
      return 1;
    default: {
      const exhaustiveCheck: never = element;
      throw new Error(`Unsupported element type "${(exhaustiveCheck as { type: string }).type}"`);
    }
  }
}

function getKey(element: TemplateElementDefinition): string {
  return element.key;
}

export function sortElementsForRendering(
  elementDefinitions: TemplateElementDefinition[]
): TemplateElementDefinition[] {
  return sortBy(elementDefinitions, [getZIndex, getTypeOrder, getKey]);
}

export interface RotationPagesMapping {
  counterpartKey: string;
  key: string;
  which: "rotated" | "non-rotated";
}

export interface RotationPagesMappingCollection {
  [key: string]: RotationPagesMapping;
}

export function buildRotationPagesMapping(
  pageDefinitions: TemplatePageDefinition[]
): RotationPagesMappingCollection {
  const mapping: RotationPagesMappingCollection = {};

  for (const { key, rotationFromPageDefinitionKey } of pageDefinitions) {
    if (rotationFromPageDefinitionKey) {
      mapping[rotationFromPageDefinitionKey] = {
        counterpartKey: key,
        key: rotationFromPageDefinitionKey,
        which: "non-rotated",
      };

      mapping[key] = {
        counterpartKey: rotationFromPageDefinitionKey,
        key,
        which: "rotated",
      };
    }
  }

  return mapping;
}

export function firstNotSetEditablePictureDefinition(
  page: ContentPage,
  template: Template
): TemplateEditablePictureDefinition | undefined {
  const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);

  const editablePictureDefinition = find(
    sortElementsForRendering(pageDefinition.elementDefinitions),
    (elementDefinition) => {
      return (
        elementDefinition.type === "editable-picture" &&
        !find(
          page.editablePictures,
          ({ editablePictureKey }) => editablePictureKey === elementDefinition.key
        )
      );
    }
  );

  if (editablePictureDefinition && editablePictureDefinition.type !== "editable-picture") {
    return undefined;
  }

  return editablePictureDefinition;
}

export function firstNotSetEditablePictureDefinitionAndPageInPageList(
  pages: ContentPage[],
  template: Template
):
  | {
      page: ContentPage;
      editablePictureDefinition: TemplateEditablePictureDefinition;
    }
  | undefined {
  const pageDefinitions: { [key: string]: TemplatePageDefinition } = {};
  template.pageDefinitions.forEach((pageDefinition) => {
    pageDefinitions[pageDefinition.key] = pageDefinition;
  });

  let targetPage: ContentPage | undefined;
  let editablePictureDefinition: undefined | TemplateElementDefinition;
  for (const page of pages) {
    const pictureDefinition = find(
      sortElementsForRendering(pageDefinitions[page.pageDefinitionKey].elementDefinitions),
      (elementDefinition) => {
        return (
          elementDefinition.type === "editable-picture" &&
          !find(
            page.editablePictures,
            ({ editablePictureKey }) => editablePictureKey === elementDefinition.key
          )
        );
      }
    );
    if (pictureDefinition) {
      targetPage = page;
      editablePictureDefinition = pictureDefinition;
      break;
    }
  }

  if (
    !targetPage ||
    !editablePictureDefinition ||
    editablePictureDefinition.type !== "editable-picture"
  ) {
    return undefined;
  }

  return { page: targetPage, editablePictureDefinition };
}

export function singleEditablePictureDefinition(
  page: ContentPage,
  template: Template
): TemplateEditablePictureDefinition | undefined {
  const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);

  const editablePictureDefinitions = pageDefinition.elementDefinitions.filter(
    (elementDefinition) => {
      return elementDefinition.type === "editable-picture";
    }
  );
  const editablePictureDefinition =
    editablePictureDefinitions.length === 1 && editablePictureDefinitions[0];

  if (!editablePictureDefinition || editablePictureDefinition.type !== "editable-picture") {
    return undefined;
  }

  return editablePictureDefinition;
}

export function selectPageDefinitionsWithEditablePicture(
  template: Template
): TemplatePageDefinition[] {
  return filter(template.pageDefinitions, ({ elementDefinitions }) =>
    some(elementDefinitions, ({ type }) => type === "editable-picture")
  );
}

export function generateNewPage(
  pageDefinitionKey: string,
  template: Template,
  defaultColorKey?: string
): ContentPage {
  const pageDefinition = findPageDefinition(pageDefinitionKey, template);

  let defaultColorDefinition: TemplateColorDefinition | undefined;
  if (defaultColorKey && pageDefinition.backgroundColorKeys.includes(defaultColorKey)) {
    defaultColorDefinition = findColorDefinition(defaultColorKey, template.colorDefinitions);
  }

  let colorValues = defaultColorDefinition
    ? defaultColorDefinition
    : findColorDefinition(pageDefinition.backgroundColorKeys[0], template.colorDefinitions);

  if (!colorValues) {
    colorValues = {
      key: pageDefinition.backgroundColorKeys[0],
      value: "#ffffff",
    };

    const fontColors = pageDefinition.elementDefinitions.map((element) => {
      if (element.type !== "editable-text" && element.type !== "forced-text") {
        return null;
      }
      return { key: element.fontColorKey, value: DEFAULT_FONT_COLOR };
    });
    if (fontColors) {
      colorValues.fontColors = compact(fontColors);
    }
  }

  const newPage: ContentPage = {
    key: uuid(),
    pageDefinitionKey,

    backgroundColorKey: colorValues.key,
    editablePictures: [],
    editableTexts: [],
  };

  return newPage;
}

export function addPageMutation(page: ContentPage, content: Content): void {
  content.pages.push(page);

  if (content.templateType === "print") {
    content.printQuantities.push({ pageKey: page.key, quantity: 1 });
  }
}

export function scopedId(id: string, elementDefinition: TemplateElementDefinition): string {
  return `${id}-${elementDefinition.key}-${uuid()}`;
}

interface KeyMapping {
  from: string;
  to: string | null;
}

interface PictureKeyMapping {
  from: string | null;
  to: string | null;
}

export function calcEditablePicturesMapping(
  editablePictures: ContentEditablePicture[],
  prevPageDefinition: TemplatePageDefinition,
  nextPageDefinition: TemplatePageDefinition
): PictureKeyMapping[] {
  const mapping: PictureKeyMapping[] = [];
  const remainingKeys = map(editablePictures, ({ editablePictureKey }) => editablePictureKey);

  const sortedPrevElementDefinitions = compact(
    map(sortElementsForRendering(prevPageDefinition.elementDefinitions), (elementDefinition) =>
      elementDefinition.type === "editable-picture" ? elementDefinition : null
    )
  );
  const sortedNextElementDefinitions = compact(
    map(sortElementsForRendering(nextPageDefinition.elementDefinitions), (elementDefinition) =>
      elementDefinition.type === "editable-picture" ? elementDefinition : null
    )
  );

  // Eliminate definitions for which we have no content.
  const notPresentElementDefinitions = differenceWith(
    sortedPrevElementDefinitions,
    remainingKeys,
    (elementDefinition, key) => elementDefinition.key === key
  );
  pullAllBy(sortedPrevElementDefinitions, notPresentElementDefinitions, "key");

  const prevKeys = map(sortedPrevElementDefinitions, ({ key }) => key);
  const nextKeys = map(sortedNextElementDefinitions, ({ key }) => key);
  const totalKeys = Math.max(prevKeys.length, nextKeys.length);

  for (let i = 0; i < totalKeys; i++) {
    const prevKeyValue = prevKeys[i] ? prevKeys[i] : null;
    const nextKeyValue = nextKeys[i] ? nextKeys[i] : null;
    mapping.push({ from: prevKeyValue, to: nextKeyValue });
  }

  return mapping;
}

export function calcEditableTextsMapping(
  editableTexts: ContentEditableText[],
  prevPageDefinition: TemplatePageDefinition,
  nextPageDefinition: TemplatePageDefinition
): KeyMapping[] {
  const mapping: KeyMapping[] = [];
  const remainingKeys = map(editableTexts, ({ editableTextKey }) => editableTextKey);

  const sortedPrevElementDefinitions = compact(
    map(sortElementsForRendering(prevPageDefinition.elementDefinitions), (elementDefinition) =>
      elementDefinition.type === "editable-text" ? elementDefinition : null
    )
  );
  const sortedNextElementDefinitions = compact(
    map(sortElementsForRendering(nextPageDefinition.elementDefinitions), (elementDefinition) =>
      elementDefinition.type === "editable-text" ? elementDefinition : null
    )
  );

  // Eliminate definitions for which we have no content.
  const notPresentElementDefinitions = differenceWith(
    sortedPrevElementDefinitions,
    remainingKeys,
    (elementDefinition, key) => elementDefinition.key === key
  );
  pullAllBy(sortedPrevElementDefinitions, notPresentElementDefinitions, "key");

  // First pass: matching by key.
  const prevKeys = map(sortedPrevElementDefinitions, ({ key }) => key);
  const nextKeys = map(sortedNextElementDefinitions, ({ key }) => key);
  const commonKeys = intersection(prevKeys, nextKeys);
  for (const commonKey of commonKeys) {
    mapping.push({ from: commonKey, to: commonKey });
    pull(remainingKeys, commonKey);
  }

  // Second pass: matching by order of rendering
  for (const elementDefinitionFrom of sortedPrevElementDefinitions) {
    if (includes(remainingKeys, elementDefinitionFrom.key)) {
      let added = false;
      for (const elementDefinitionTo of sortedNextElementDefinitions) {
        if (
          !added &&
          !includes(
            map(mapping, ({ to }) => to),
            elementDefinitionTo.key
          )
        ) {
          mapping.push({
            from: elementDefinitionFrom.key,
            to: elementDefinitionTo.key,
          });
          pull(remainingKeys, elementDefinitionFrom.key);
          added = true;
        }
      }
    }
  }

  // Last pass: remaining
  for (const key of remainingKeys) {
    mapping.push({ from: key, to: null });
  }

  return mapping;
}

export function isEmptyPage(page: ContentPage): boolean {
  if (page.editablePictures.length > 0 || page.editableTexts.length > 0) {
    return false;
  }
  return true;
}

export function getNumberPagesToAdd(
  template: Template,
  pageDefinition: TemplatePageDefinition,
  configuration: Configuration | undefined
): number {
  let nbPagesToAdd = 1;
  const specialBookPages = [
    "back-cover",
    "front-cover",
    "layflat-back-matter",
    "layflat-front-matter",
    "single-back-matter",
    "single-front-matter",
    "spine",
  ];
  if (
    template.templateType === "book" &&
    template.pageMode === "single" &&
    specialBookPages.includes(pageDefinition.type) === false
  ) {
    nbPagesToAdd = 2;
  }
  if (
    template.templateType !== "print" &&
    configuration &&
    configuration.pages &&
    configuration.pages.increment &&
    configuration.pages.increment > 1 &&
    specialBookPages.includes(pageDefinition.type) === false
  ) {
    nbPagesToAdd = configuration.pages.increment;
  }
  return nbPagesToAdd;
}

export function hasEmptyEditablePictures(
  page: {
    pageDefinitionKey: string;
    editablePictures: ContentEditablePicture[];
  },
  template: Template
): boolean {
  const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);
  const wantedEditablePictures = filter(
    pageDefinition.elementDefinitions,
    (definition) => definition.type === "editable-picture"
  );
  return page.editablePictures.length !== wantedEditablePictures.length;
}

export function countEmptyEditablePictures(pages: ContentPage[], template: Template): number {
  const editablePicturesCount = pages.reduce((count: number, page) => {
    const pageDefinition = findPageDefinition(page.pageDefinitionKey, template);
    const wantedEditablePictures = filter(
      pageDefinition.elementDefinitions,
      (definition) => definition.type === "editable-picture"
    );
    return count + (wantedEditablePictures.length - page.editablePictures.length);
  }, 0);
  return editablePicturesCount;
}

export function findSelectionPhotosInPages(
  pages: ContentPage[],
  selection: SelectionPhoto[]
): SelectionPhoto[] {
  const selectionPhotos: SelectionPhoto[] = [];

  pages.forEach((page) => {
    page.editablePictures.forEach((editablePicture) => {
      const selectedPicture = selection.find(
        (picture) => picture.key === editablePicture.selectionPhotoKey
      );
      if (selectedPicture) {
        selectionPhotos.push(selectedPicture);
      }
    });
  });

  return selectionPhotos;
}

export function getPagePosition(pages: ContentPage[], pageKey: string): number {
  let pageNumber = 0;

  for (let index = 0; index < pages.length; index++) {
    if (pages[index].key === pageKey) {
      pageNumber = index + 1;
      break;
    }
  }

  return pageNumber;
}

export function getPageLabel(
  configuration: Configuration | undefined,
  templateType: "book" | "mural" | "print"
): { label: string | null; translationId: string } {
  let pageLabel: string | null = null;

  if (configuration && configuration.pageName) {
    pageLabel = configuration.pageName.singular;
  } else if (templateType !== "book" && templateType !== "mural" && templateType !== "print") {
    throw new Error(`Unsupported templateType "${templateType as string}"`);
  }

  return {
    label: pageLabel,
    translationId: `kustom_PageList_PageName__${templateType}`,
  };
}

export function findPageDescription(
  pageKey: string,
  pagesConfigMapping: PageConfigMapping[]
): LocalizedContent[] | null {
  if (pagesConfigMapping.length === 0) {
    return null;
  }

  const pageConfig = pagesConfigMapping.find((pageMapping) => pageMapping.pageKey === pageKey);

  if (pageConfig === undefined) {
    const error = new Error(`Can't find pageDescription in pages mapping for pageKey "${pageKey}"`);
    bugsnag.notify(error);
    return null;
  }

  return pageConfig.description && pageConfig.description.length > 0
    ? pageConfig.description
    : null;
}

export function getPageTitle(
  pageKey: string,
  pagesConfigMapping: PageConfigMapping[],
  locale: TemplateLocale,
  configuration: Configuration | undefined,
  templateType: "book" | "mural" | "print",
  intl: { formatMessage(descriptor: MessageDescriptor): string },
  pageNumber: number
): string | null {
  const localizedDescription = findPageDescription(pageKey, pagesConfigMapping);
  const pageDescription =
    localizedDescription && findLocalizedContent(localizedDescription, locale);
  if (pageDescription) {
    return pageDescription.content;
  }

  const { label, translationId } = getPageLabel(configuration, templateType);
  if (label) {
    return `${label} ${pageNumber}`;
  }
  if (translationId) {
    return `${intl.formatMessage({ id: translationId })} ${pageNumber}`;
  }
  return null;
}
